import { NetworkStatus, useQuery } from '@apollo/client'
import { ProjectElementType } from '@deltek/specification-client/core/Models/projectElement'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { makeStyles, Theme } from '@material-ui/core/styles'
import { GetProjectElementNodesQuery } from 'api/graphQL/queries/projectElements.queries'
import { GetProjectElementNodesResponse } from 'api/graphQL/types/projectElement.types'
import clsx from 'clsx'
import { ExecutionResult } from 'graphql'
import { GetProjectElementNodesInput } from 'models/getProjectElementNodesInput'
import {
    CheckForDuplicateFilesInAssemblyInput,
    ImportUploadFileInfo,
} from 'models/projectImport'
import { ProjectPageContext } from 'pages/ProjectsPage/ProjectPage/components/ProjectPageContext'
import { CategoryElementListStateInfo } from 'pages/ProjectsPage/WorkspacePage'
import ElementsPanel, {
    ProjectElementForImport,
} from 'pages/ProjectsPage/WorkspacePage/components/ElementsPanel/ElementsPanel'
import { ElementItemIdentifier } from 'pages/ProjectsPage/WorkspacePage/components/ElementsPanel/ElementTreeView'
import { StudioContext } from 'pages/StudioPage/common/StudioContext'
import React from 'react'

const useStyles = makeStyles((theme: Theme) => ({
    panel: {
        overflowX: 'hidden',
        transition: '100ms',
        // boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
        zIndex: 1,
        '& *::-webkit-scrollbar': {
            width: 6,
            height: 20,
        },
        '& *::-webkit-scrollbar-thumb': {
            backgroundColor: '#464646',
            borderRadius: 100,
        },
        '& *::-webkit-scrollbar-track': {
            boxShadow: 'none',
        },
    },
    collapsedPane: {
        width: 0,
        padding: theme.spacing(0),
    },
    expandedPane: {
        width: 485,
        padding: theme.spacing(1),
    },
    fullWidthPaneContentContainer: {
        width: '100%',
        height: '100%',
        overflowY: 'auto',
        overflowX: 'hidden',
    },
    fullWidthPaneCategoryContainer: {
        width: 318,
        paddingRight: theme.spacing(1),
        height: '100%',
        overflow: 'auto',
        '&:focus': {
            backgroundColor: theme.palette.common.white,
        },
        '&::-webkit-scrollbar': {
            width: 6,
            height: 20,
        },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#464646',
            borderRadius: 100,
        },
        '&::-webkit-scrollbar-track': {
            boxShadow: 'none',
            marginTop: theme.spacing(2.5),
        },
    },
    categoriesPane: {
        backgroundColor: '#EDF2F2',
    },
    elementsPane: {
        backgroundColor: '#F2F2F2',
    },
    mainContentPane: {
        backgroundColor: theme.palette.common.white,
    },
    container: {
        bottom: 0,
        maxWidth: 1920,
        flexWrap: 'nowrap',
        [theme.breakpoints.down(theme.breakpoints.values.xl + 1)]: {
            left: 0,
        },
    },
    showHideContainer: {
        backgroundColor: '#C27A40',
        height: 40,
        position: 'absolute',
        left: 0,
        top: 0,
    },
    backdrop: {
        zIndex: theme.zIndex.drawer + 1,
        color: theme.palette.common.white,
    },
    padded: {
        padding: theme.spacing(2),
        paddingTop: theme.spacing(0),
        marginBottom: theme.spacing(4),
    },
    searchBarContainer: {
        background: '#F2F2F2',
        height: pxToRem(70),
        position: 'absolute',
        borderBottom: '1px solid #C9C9C9',
        left: 0,
    },
    customWidgetContainer: {
        display: 'flex',
        flexDirection: 'column',
        padding: theme.spacing(0, 1.25),
        marginTop: theme.spacing(0.25),
        transition: '100ms',
        minWidth: pxToRem(60),
    },
}))

export interface ElementsPanelImportProps {
    projectId?: string
    selectedFile?: Partial<ImportUploadFileInfo> | null
    getAssociationLists?: (
        data: CheckForDuplicateFilesInAssemblyInput[],
        isPT?: boolean,
        removedElementTreePath?: string
    ) => void
    showGlobalSnackbar?: (options?: any) => Promise<ExecutionResult<any>>
    associations?: CheckForDuplicateFilesInAssemblyInput[]
    resetElements?: boolean
    handleResetElements?: (value: boolean) => void
    isBPImport?: boolean
}

const ElementsPanelImport: React.FC<ElementsPanelImportProps> = ({
    projectId,
    selectedFile,
    getAssociationLists,
    showGlobalSnackbar,
    resetElements,
    handleResetElements,
    isBPImport,
}) => {
    const classes = useStyles()
    const selectedCategory = { id: 'all', name: 'All Categories' }

    const {
        categoryElementStates,
        setCategoryElementStates,
        selectedElementIdentifier,
        setSelectedElementIdentifier,
        treeIndexerRef,
        projectData,
    } = React.useContext(ProjectPageContext)
    const [skipDefaultQuery, setSkipDefaultQuery] = React.useState<boolean>(
        false
    )
    const [elementListImport, setElementListImport] = React.useState<
        Record<string, CategoryElementListStateInfo>
    >({})
    const { treeIndexerRefStudio } = React.useContext(StudioContext)
    const [isFetchingMoreElements, setIsFetchingMoreElements] = React.useState(
        false
    )

    const [
        showElementsPanelFullLoadState,
        setShowElementsPanelFullLoadState,
    ] = React.useState(false)
    const elementStates:
        | CategoryElementListStateInfo
        | undefined = React.useMemo(() => {
        return (
            categoryElementStates?.[selectedCategory.id] ??
            elementListImport?.['all'] ??
            {}
        )
    }, [categoryElementStates, elementListImport, selectedCategory])
    const [isNewDataLoading, setIsNewDataLoading] = React.useState<boolean>(
        false
    )
    const treeIndexerLocal = React.useMemo(
        () => treeIndexerRef ?? treeIndexerRefStudio,
        [treeIndexerRef, treeIndexerRefStudio]
    )

    const getUpdatedTreeIndexer = React.useCallback(
        (
            data: GetProjectElementNodesResponse,
            currentElementFlatList: CategoryElementListStateInfo[]
        ) => {
            const currentTreeIndexer = treeIndexerLocal?.current
            data.getProjectElementNodes.projectElementNodes.forEach(
                (each: ProjectElementForImport, index: number) => {
                    if (!currentTreeIndexer?.has(each.treePath)) {
                        currentTreeIndexer?.set(
                            each.treePath,
                            new Map<string, number>()
                        )
                    }
                    const categoryIndexer = currentTreeIndexer?.get(
                        each.treePath
                    )
                    categoryIndexer?.set(
                        selectedCategory?.id!,
                        currentElementFlatList.length + index
                    )
                }
            )
            return currentTreeIndexer
        },
        [treeIndexerLocal, selectedCategory]
    )

    const updateCategoryElementState = React.useCallback(
        (data: GetProjectElementNodesResponse, forceReset: boolean = false) => {
            const currentCategoryElementStates = Object.assign(
                elementStates
                    ? elementStates
                    : {
                          data: null,
                          hasMoreElementsToFetch: false,
                          totalElementCount: 0,
                          elementSkipCount: 0,
                      }
            )

            const currentElementFlatList = [
                ...(forceReset ? [] : currentCategoryElementStates.data ?? []),
            ]

            //reset treeIndexer too if forceReset is true
            if (forceReset) {
                treeIndexerLocal?.current.clear()
            }

            treeIndexerLocal!.current = getUpdatedTreeIndexer(
                data,
                currentElementFlatList
            )!
            const isForSearch: boolean = false

            const parentIndex =
                currentElementFlatList.findIndex(
                    (element) =>
                        element.id ===
                            data.getProjectElementNodes.projectElementNodes?.[0]
                                ?.parentNodeId ||
                        element.treePath ===
                            data.getProjectElementNodes.projectElementNodes?.[0]
                                ?.parentTreePath
                ) + 1
            const concatenatedList = !isForSearch
                ? [
                      ...(currentCategoryElementStates.elementSkipCount > 0
                          ? currentElementFlatList
                          : []
                      ).slice(0, parentIndex),
                      ...data.getProjectElementNodes.projectElementNodes,
                      ...(currentCategoryElementStates.elementSkipCount > 0
                          ? currentElementFlatList
                          : []
                      ).slice(parentIndex),
                  ]
                : [
                      ...(currentCategoryElementStates.elementSkipCount > 0
                          ? currentElementFlatList
                          : []),
                      ...data.getProjectElementNodes.projectElementNodes,
                  ]

            currentCategoryElementStates.hasMoreElementsToFetch = isForSearch
                ? data.getProjectElementNodes.totalCount >
                  concatenatedList.length
                : false

            currentCategoryElementStates.elementSkipCount =
                (currentCategoryElementStates.elementSkipCount ?? 0) + 1
            currentCategoryElementStates.data = concatenatedList
            currentCategoryElementStates.totalElementCount =
                data?.getProjectElementNodes.totalCount

            const baseVal = forceReset
                ? {}
                : categoryElementStates ?? elementListImport
            const newVal = {
                ...baseVal,
                [selectedCategory?.id ?? 'all']: currentCategoryElementStates,
            }
            if (setCategoryElementStates) {
                setCategoryElementStates?.(newVal)
            } else {
                setElementListImport(newVal)
            }
        },
        [
            categoryElementStates,
            elementStates,
            selectedCategory,
            setCategoryElementStates,
            treeIndexerLocal,
            getUpdatedTreeIndexer,
            elementListImport,
        ]
    )
    const {
        loading: elementsListLoading,
        fetchMore: fetchMoreElements,
        networkStatus: elementsListNetworkStatus,
    } = useQuery<
        GetProjectElementNodesResponse | null,
        GetProjectElementNodesInput
    >(GetProjectElementNodesQuery, {
        fetchPolicy: 'no-cache',
        nextFetchPolicy: 'cache-first',
        notifyOnNetworkStatusChange: true,
        variables: {
            input: {
                projectId: projectId,
                limit: 1000,
                parentNodeId: 'root',
                skip: 0,
                onlyElementsAssignedToProject: false,
                projectElementType: ['ASSEMBLY'] as ProjectElementType[],
            },
        },
        skip: skipDefaultQuery,
        onCompleted: (data: GetProjectElementNodesResponse | null) => {
            if (resetElements) {
                handleResetElements?.(false)
                updateCategoryElementState(data!)

                setShowElementsPanelFullLoadState(false)
            }
            if (
                Object.keys(categoryElementStates ?? elementListImport)
                    .length === 0
            ) {
                updateCategoryElementState(data!)

                setShowElementsPanelFullLoadState(false)
            }
        },
    })
    const handleElementItemCardClick = React.useCallback(
        (newSelectedElementIdentifier?: ElementItemIdentifier) => {
            setSelectedElementIdentifier?.(newSelectedElementIdentifier)
        },
        [setSelectedElementIdentifier, selectedElementIdentifier]
    )
    const getMoreElementsList = React.useCallback(
        (
            forceReset: boolean = false,
            parentNodeId?: string,
            elementId?: string
        ) => {
            const defaultInput = {
                projectId: projectId,
                limit: 1000,
                projectElementType: Object.keys(
                    ProjectElementType
                ) as ProjectElementType[],
                parentNodeId: parentNodeId,
                elementId: elementId,
            }
            const skipValueForDefault =
                forceReset || parentNodeId !== undefined
                    ? 0
                    : (elementStates?.elementSkipCount ?? 0) * 1000
            fetchMoreElements?.({
                variables: {
                    input: {
                        ...defaultInput,
                        skip: skipValueForDefault,
                    },
                },
                updateQuery: (
                    _,
                    {
                        fetchMoreResult,
                    }: {
                        fetchMoreResult?:
                            | GetProjectElementNodesResponse
                            | null
                            | undefined
                    }
                ) => {
                    updateCategoryElementState(fetchMoreResult!, forceReset)
                    setShowElementsPanelFullLoadState(false)
                    setIsFetchingMoreElements(false)
                    setIsNewDataLoading(false)
                    if (skipDefaultQuery) {
                        setSkipDefaultQuery(false)
                    }
                    return null
                },
            })
        },
        [
            fetchMoreElements,
            projectId,
            elementStates,
            skipDefaultQuery,
            updateCategoryElementState,
        ]
    )

    // Do initial fetch of elements list data
    React.useEffect(() => {
        if (resetElements) {
            getMoreElementsList(true, 'root')
        }
        if (
            Object.keys(categoryElementStates ?? elementListImport ?? {})
                .length > 0 &&
            isFetchingMoreElements
        ) {
            if ((elementStates?.elementSkipCount ?? 0) === 0) {
                getMoreElementsList(true, 'root')
            }
        }
    }, [
        getMoreElementsList,
        isFetchingMoreElements,
        elementStates,
        categoryElementStates,
        elementListImport,
        resetElements,
    ])
    return (
        <GridContainer>
            <GridItem
                className={clsx(
                    classes.elementsPane,
                    classes.panel,
                    classes.expandedPane
                )}
            >
                <div className={classes.fullWidthPaneContentContainer}>
                    <ElementsPanel
                        panelTitle="Assemblies and Systems"
                        calledFromImportPage={true}
                        showGlobalSnackbar={showGlobalSnackbar}
                        selectedElementIdentifier={selectedElementIdentifier}
                        isNewDataLoading={isNewDataLoading}
                        onLoadMoreClick={getMoreElementsList}
                        hasMoreElementsToFetch={
                            elementStates?.hasMoreElementsToFetch
                        }
                        projectId={isBPImport ? undefined : projectId}
                        getAssociationLists={getAssociationLists}
                        onSelectedItemIdentifierChange={
                            handleElementItemCardClick
                        }
                        selectedFileData={selectedFile}
                        elementFlatList={
                            categoryElementStates?.[selectedCategory.id]
                                ?.data ?? elementListImport?.['all']?.data
                        }
                        showFullLoadState={showElementsPanelFullLoadState}
                        isLoading={
                            resetElements ||
                            elementsListLoading ||
                            elementsListNetworkStatus !== NetworkStatus.ready ||
                            !categoryElementStates?.[selectedCategory.id]
                                ?.data ||
                            showElementsPanelFullLoadState
                        }
                        totalElementCount={elementStates?.totalElementCount}
                        famNumberFormat={
                            projectData?.getProject?.projectSettings
                                ?.familyNumberFormat
                        }
                        onLoadChildren={(id: string) =>
                            getMoreElementsList(false, id)
                        }
                        isBPImport={isBPImport}
                    />
                </div>
            </GridItem>
        </GridContainer>
    )
}
export const getElementNestingLevel = (subTextCode: string, type: string) => {
    let currentItemLevel = 1

    switch (type) {
        case 'ASSEMBLY':
            //determine assembly level
            currentItemLevel = getAssemblyLevel(subTextCode)
            break
        case 'PRODUCTFAMILY':
            currentItemLevel = 4
            break
        case 'PRODUCTTYPE':
            currentItemLevel = 5
            break
    }

    return currentItemLevel
}

export const getAssemblyLevel = (subTextCode: string) => {
    //determine assembly level
    const assemblyNumberSplit = subTextCode.split('.')
    let assemblyLevel = 1
    if (assemblyNumberSplit.length > 1) {
        assemblyLevel += assemblyNumberSplit[1].length > 2 ? 2 : 1
    }

    return assemblyLevel
}

export const getSubtextCode = (elementText: string) => {
    const textSplit = elementText.split('|')
    const subTextCode = textSplit[0]
    return subTextCode
}

export default ElementsPanelImport
